import { TextArea, TextInput } from "../ui/Field";

export interface LoadFormValues {
  originText: string;
  destinationText: string;
  linehaulDollars: string;
  fscPerMileDollars: string;
  accessorialsDollars: string;
  loadedMiles: string;
  deadheadMiles: string;
  weight: string;
  width: string;
  height: string;
  length: string;
  note: string;
  brokerName: string;
  refNumber: string;
  /** 'YYYY-MM-DD'; CheckScreen defaults it to today in the org's time zone. */
  loadDate: string;
}

export const EMPTY_LOAD_FORM: LoadFormValues = {
  originText: "",
  destinationText: "",
  linehaulDollars: "",
  fscPerMileDollars: "",
  accessorialsDollars: "",
  loadedMiles: "",
  deadheadMiles: "",
  weight: "",
  width: "",
  height: "",
  length: "",
  note: "",
  brokerName: "",
  refNumber: "",
  loadDate: "",
};

export function LoadForm({
  values,
  onChange,
  onBrokerCommit,
}: {
  values: LoadFormValues;
  onChange: (next: LoadFormValues) => void;
  /** Broker field lost focus: link/create the broker record (not per keystroke). */
  onBrokerCommit?: (name: string) => void;
}) {
  const set =
    (key: keyof LoadFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ ...values, [key]: e.target.value });
    };

  return (
    <div className="card stack" data-testid="load-form">
      <div className="row">
        <div style={{ flex: 1, minWidth: 140 }}>
          <TextInput
            label="Origin"
            placeholder="City, ST"
            value={values.originText}
            onChange={set("originText")}
            autoComplete="off"
          />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <TextInput
            label="Destination"
            placeholder="City, ST"
            value={values.destinationText}
            onChange={set("destinationText")}
            autoComplete="off"
          />
        </div>
      </div>
      <div className="row">
        <div style={{ flex: 1, minWidth: 110 }}>
          <TextInput
            label="Linehaul $"
            placeholder="0.00"
            inputMode="decimal"
            value={values.linehaulDollars}
            onChange={set("linehaulDollars")}
          />
        </div>
        <div style={{ flex: 1, minWidth: 110 }}>
          <TextInput
            label="FSC $/mi"
            placeholder="0.00"
            inputMode="decimal"
            value={values.fscPerMileDollars}
            onChange={set("fscPerMileDollars")}
          />
        </div>
        <div style={{ flex: 1, minWidth: 110 }}>
          <TextInput
            label="Accessorials $"
            placeholder="0.00"
            inputMode="decimal"
            value={values.accessorialsDollars}
            onChange={set("accessorialsDollars")}
          />
        </div>
      </div>
      <div className="row">
        <div style={{ flex: 1, minWidth: 110 }}>
          <TextInput
            label="Loaded mi"
            placeholder="0"
            inputMode="decimal"
            value={values.loadedMiles}
            onChange={set("loadedMiles")}
          />
        </div>
        <div style={{ flex: 1, minWidth: 110 }}>
          <TextInput
            label="Deadhead mi"
            placeholder="0"
            inputMode="decimal"
            value={values.deadheadMiles}
            onChange={set("deadheadMiles")}
          />
        </div>
      </div>
      <div className="row">
        <div style={{ flex: 1, minWidth: 100 }}>
          <TextInput
            label="Weight"
            placeholder="lb"
            value={values.weight}
            onChange={set("weight")}
          />
        </div>
        <div style={{ flex: 1, minWidth: 100 }}>
          <TextInput
            label="Width"
            placeholder="in or ft"
            value={values.width}
            onChange={set("width")}
          />
        </div>
        <div style={{ flex: 1, minWidth: 100 }}>
          <TextInput
            label="Height"
            placeholder="in or ft"
            value={values.height}
            onChange={set("height")}
          />
        </div>
        <div style={{ flex: 1, minWidth: 100 }}>
          <TextInput
            label="Length"
            placeholder="in or ft"
            value={values.length}
            onChange={set("length")}
          />
        </div>
      </div>
      <div className="row">
        <div style={{ flex: 1, minWidth: 140 }}>
          <TextInput
            label="Broker"
            placeholder="Broker name"
            value={values.brokerName}
            onChange={set("brokerName")}
            onBlur={(e) => onBrokerCommit?.(e.target.value)}
            autoComplete="off"
            aria-label="Broker"
          />
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <TextInput
            label="Ref #"
            placeholder="Load ref"
            value={values.refNumber}
            onChange={set("refNumber")}
            autoComplete="off"
            aria-label="Ref #"
          />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <TextInput
            label="Load date"
            type="date"
            value={values.loadDate}
            onChange={set("loadDate")}
            aria-label="Load date"
            data-testid="load-date"
          />
        </div>
      </div>
      <TextArea
        label="Note"
        placeholder="Optional note"
        value={values.note}
        onChange={set("note")}
      />
    </div>
  );
}
